<template>
  <assistant-step :title="$t(`${step.id}.title`)">
    <gc-expansion-panels multiple v-model="panels" class="gc-panels mb-8">
      <gc-expansion-panel
        class="pt-4"
        v-for="summaryCategory in summary"
        :key="summaryCategory.id"
      >
        <gc-expansion-panel-header
          class="gc-panel-header"
          v-if="summary.length > 1 && summaryCategory.categoryTitle"
        >
          {{ $t(`${summaryCategory.categoryTitle}`) }}
        </gc-expansion-panel-header>
        <gc-expansion-panel-content>
          <div
            v-for="(summaryStep, summaryStepIndex) in summaryCategory.steps"
            :key="summaryStep.id"
          >
            <div class="d-flex justify-space-between mb-4">
              <div class="font-weight-bold text-subtitle-2">
                {{ summaryStep.title }}
              </div>
              <gc-button
                class="mr-n4"
                text
                small
                color="primary"
                @click="editStep(summaryStep.id)"
              >
                <font-awesome-icon :icon="faPen" class="mr-2" />
                <span class="font-weight-bold d-none d-sm-flex">
                  {{ $t('edit') }}
                </span>
              </gc-button>
            </div>
            <ul>
              <li
                v-for="(field, fieldIndex) in summaryStep.fields"
                :key="fieldIndex"
              >
                <gc-row
                  class="mb-1"
                  v-if="
                    field.formattedLabel !== false ||
                    field.formattedValue !== false
                  "
                >
                  <gc-col
                    cols="12"
                    sm="8"
                    v-if="field.formattedLabel !== false"
                  >
                    <div class="text-body-2">
                      {{ field.formattedLabel }}
                    </div>
                  </gc-col>
                  <gc-col
                    class="text-right"
                    cols="12"
                    sm="4"
                    v-if="field.formattedLabel !== false"
                  >
                    <div class="text-body-2 break-words">
                      {{ field.formattedValue }}
                    </div>
                  </gc-col>
                  <gc-col cols="12" v-else>
                    <div class="text-body-2">
                      {{ field.formattedValue }}
                    </div>
                  </gc-col>
                </gc-row>
              </li>
            </ul>
            <gc-divider
              class="mb-6"
              v-if="summaryStepIndex < summaryCategory.steps.length - 1"
            />
          </div>
        </gc-expansion-panel-content>
      </gc-expansion-panel>
    </gc-expansion-panels>

    <assistant-step-navigation
      :assistantContext="assistantContext"
      :step="step"
      :steps="steps"
      :submit="step.submit"
      :show-back-button="showBackButton()"
      @next="onNext()"
      @prev="onPrev()"
    />
  </assistant-step>
</template>

<script lang="ts">
import { defineComponent, ref } from '@vue/composition-api';
import { faPen } from '@fortawesome/pro-solid-svg-icons';
import { OutputFormatterTypes } from '@/consts/output-formatter-types';
import { getFormattedData } from '@/lib/formatter/context';
import GcDivider from '@/components/primitives/GcDivider.vue';
import GcButton from '@/components/primitives/GcButton.vue';
import GcCol from '@/components/primitives/GcCol.vue';
import GcForm from '@/components/primitives/GcForm.vue';
import GcExpansionPanel from '@/components/primitives/GcExpansionPanel.vue';
import GcExpansionPanelContent from '@/components/primitives/GcExpansionPanelContent.vue';
import GcExpansionPanelHeader from '@/components/primitives/GcExpansionPanelHeader.vue';
import GcExpansionPanels from '@/components/primitives/GcExpansionPanels.vue';
import GcRow from '@/components/primitives/GcRow.vue';
import AssistantStep from '@/components/AssistantStep.vue';
import AssistantStepNavigation from '@/components/AssistantStepNavigation.vue';
import { stepComponentProps } from '@/lib/steps/helper';
import FormField from '@/components/FormField.vue';
import { useBaseTransitions } from '@/lib/xstate/transitions';

export default defineComponent({
  components: {
    AssistantStep,
    AssistantStepNavigation,
    FormField,
    GcButton,
    GcCol,
    GcDivider,
    GcExpansionPanel,
    GcExpansionPanelContent,
    GcExpansionPanelHeader,
    GcExpansionPanels,
    GcForm,
    GcRow,
  },
  props: {
    ...stepComponentProps,
  },
  setup: (props, { emit }) => {
    const { onNext, onPrev } = useBaseTransitions(
      emit,
      props.assistantContext,
      props.step,
    );

    const panels = ref([0]);

    const currentStepIndex =
      props.step && props.steps && props.steps.indexOf(props.step);
    const showBackButton = () =>
      props.step && props.steps && props.steps.indexOf(props.step) > 0;

    const summary = getFormattedData({
      assistantContext: props.assistantContext,
      currentStepIndex,
      steps: props.steps ?? [],
      target: OutputFormatterTypes.SUMMARY,
    });

    const editStep = (stepId: string) => {
      emit('transition', {
        event: `BACK_TO_${stepId.toUpperCase()}`,
      });
    };

    return {
      editStep,
      faPen,
      onNext,
      onPrev,
      panels,
      showBackButton,
      summary,
    };
  },
});
</script>
<style lang="scss">
.gc-panels {
  ul {
    padding-left: 0;
  }
}
.gc-panel-header {
  .v-icon__component {
    width: 12px;
    height: 12px;
  }
}
</style>
